import { PlusOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Table } from 'antd'
import { Dispatch, SetStateAction, useState } from 'react'

import { usePreScreeningServiceGetRiskGroupListEndpoint } from 'src/api/queries'
import { Permissions, RiskGroupStatus } from 'src/api/requests'
import { PageBreadcrumbs, SearchBar, Select } from 'src/components'
import { useDefaultSortingItem, useTablePagination, useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

import { AddListModal } from '../components'
import { useConfig } from '../hooks'

type ModalTypeProps = keyof typeof modalMap | null

const modalMap = {
  create: AddListModal,
  edit: AddListModal
}

export const RiskGroupsPage = () => {
  const [modalType, setModalType] = useState<ModalTypeProps>(null)
  const [groupId, setGroupId] = useState<string>()

  const { hasPermission } = useUser()

  const onClose = () => {
    setModalType(null)
  }

  const { t } = useLocales()
  const pagination = useTablePagination()

  const onAction = (type: ModalTypeProps, id?: string) => {
    setGroupId(id)
    setModalType(type)
  }

  const menuItems = {
    onCreate: () => onAction('create'),
    onEdit: (id?: string) => onAction('edit', id)
  }

  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')
  const [status, setStatus] = useState<RiskGroupStatus>()

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    status: hasPermission(Permissions.PRE_SCREENING_RISK_GROUPS_SUPPORT_ALL)
      ? RiskGroupStatus.SENT
      : (status ?? undefined),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering),
      sortDirection: getOrderingDirection(ordering)
    })
  }

  const { data, isLoading } =
    usePreScreeningServiceGetRiskGroupListEndpoint(queryParams)

  const { columns, isDeleting, orderingOptions } = useConfig(menuItems)

  const onFilterChange = <T,>(
    val: T,
    setState: Dispatch<SetStateAction<T>>
  ) => {
    setState(val)
    resetPagination(pagination)
  }

  const statusOptions = [
    defaultSortingOption,
    ...Object.values(RiskGroupStatus).map((item) => ({
      value: item,
      label: t(`pre_screening.${item}`)
    }))
  ]

  const renderModal = () => {
    if (!modalType) return null

    const ModalComponent = modalMap[modalType]

    return <ModalComponent id={groupId} onCancel={onClose} destroyOnClose />
  }

  return (
    <PageBreadcrumbs
      title={t('menu.ong_support')}
      items={[
        {
          title: t('menu.ong_support')
        }
      ]}
    >
      <Flex gap={16} justify="space-between" wrap="wrap">
        <Flex wrap="wrap" gap={16}>
          <SearchBar onSearch={(val) => onFilterChange(val, setSearch)} />
        </Flex>

        <Flex gap={16} wrap="wrap">
          <Select
            width={188}
            options={orderingOptions}
            onSelect={(val) => onFilterChange(val, setOrdering)}
          />

          {hasPermission(Permissions.PRE_SCREENING_RISK_GROUPS_CREATE_ALL) && (
            <>
              <Select
                width={188}
                options={statusOptions}
                onSelect={(val) => onFilterChange(val, setStatus)}
              />

              <Button
                icon={<PlusOutlined />}
                onClick={() => onAction('create')}
                type="primary"
              >
                {t('pre_screening.add_new_list')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        columns={columns}
        loading={isLoading || isDeleting}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {renderModal()}
    </PageBreadcrumbs>
  )
}
